import { call, fork, put, select, take, takeEvery } from 'redux-saga/effects';

import { exportToYms, getFenixYmsHistory } from 'services/transactionService';
import {
    selectTransactionFormStateByTransactionId,
    selectFormConfigByTransactionId,
    selectTransactionDataByTransactionId,
} from 'store/formControlSlice/formControlSelectors';
import { formControlActions } from 'store/formControlSlice/formControlSlice';

import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { selectTransactionsSplitViewMode } from 'store/transactionsSlice/transactionsSelectors';
import { selectUser } from 'store/authSlice/authSelectors';

import { fenixYmsActions } from './fenixYmsSlice';
import { selectFenixExportButtonLockedByTransactionId, selectFenixIsInProgressByTransactionId } from './fenixYmsSelectors';
import {
    YMS_INIT_STATUS,
    YES_NO_UNKNOWN,
    FENIX_EXPORTED_TO_YMS_FIELD,
    FENIX_YMS_COPY,
    mapExportedToYmsDisplay,
    normalizeAttempt,
    resolveLookupValueFromDisplay,
} from 'utils/fenixYms';

const CONFLICT_STATUS = 409;

function* takeLeadingPerKey(type, saga, keySelector) {
    const activeTasks = {};
    while (true) {
        const action = yield take(type);
        const key = keySelector(action);
        if (key == null) continue;
        const running = activeTasks[key];
        if (running && running.isRunning()) continue;
        activeTasks[key] = yield fork(saga, action);
    }
}

export function buildEffectiveFormValues(formConfig, values, automation) {
    const effective = { ...(values || {}) };
    const fields = formConfig?.fields || [];
    fields.forEach((field) => {
        const key = field?.dataField;
        if (!key || key === FENIX_EXPORTED_TO_YMS_FIELD) return;
        const current = effective[key];
        const hasValue = current !== undefined && current !== null && current !== '';
        if (hasValue) return;
        const suggestion = automation?.[key]?.sourceValue;
        if (suggestion === undefined || suggestion === null || suggestion === '') return;
        effective[key] = resolveLookupValueFromDisplay(suggestion, field?.lookup);
    });
    return effective;
}

function buildExportPayload(transaction, formValues, user) {
    const location = transaction?.gate?.location || {};
    return {
        transactionId: transaction?.id ?? null,
        location: {
            id: location.id ?? transaction?.locationId ?? null,
            yardId: location.name ?? transaction?.locationName ?? null,
            name: location.fullName ?? location.name ?? transaction?.locationName ?? null,
        },
        gate: {
            id: transaction?.gate?.id ?? transaction?.gateId ?? null,
            name: transaction?.gate?.name ?? transaction?.gateName ?? null,
        },
        requestTimestamp: new Date().toISOString(),
        agent: {
            id: user?.id ?? null,
            username: user?.username ?? null,
        },
        formValues: formValues || {},
    };
}

function* autoSuggestExportedToYms(transactionId, suggestedExportedToYms) {
    if (!suggestedExportedToYms) return;

    const formState = yield select(selectTransactionFormStateByTransactionId(transactionId));
    const fieldExists = !!formState?.values && Object.prototype.hasOwnProperty.call(formState.values, FENIX_EXPORTED_TO_YMS_FIELD);
    if (!fieldExists) return;

    const isSplitView = yield select(selectTransactionsSplitViewMode);
    const display = mapExportedToYmsDisplay(suggestedExportedToYms);
    yield put(formControlActions.setExportedToYmsSuggestion({ formId: transactionId, sourceValue: display, writeFieldValue: isSplitView }));
}

function* exportToYmsStartSaga({ payload: { transaction } }) {
    const transactionId = transaction?.id;
    if (!transactionId) return;

    const inProgress = yield select(selectFenixIsInProgressByTransactionId(transactionId));
    const locked = yield select(selectFenixExportButtonLockedByTransactionId(transactionId));
    if (inProgress || locked) return;

    const formState = yield select(selectTransactionFormStateByTransactionId(transactionId));
    const formConfig = yield select(selectFormConfigByTransactionId(transactionId));
    const user = yield select(selectUser);
    const effectiveValues = buildEffectiveFormValues(formConfig, formState?.values, formState?.automation);

    const transactionData = yield select(selectTransactionDataByTransactionId(transactionId));
    const detailedTransaction = transactionData ? { ...transaction, ...transactionData } : transaction;

    const payload = buildExportPayload(detailedTransaction, effectiveValues, user);

    yield put(fenixYmsActions.requestTabActivation({ transactionId }));
    yield put(fenixYmsActions.exportStarted({ transactionId }));

    try {
        const response = yield call(exportToYms, payload);

        if (response?.status === YMS_INIT_STATUS.ERROR) {
            yield put(fenixYmsActions.exportInitFailed({ transactionId, message: response?.message }));
            yield call(autoSuggestExportedToYms, transactionId, YES_NO_UNKNOWN.NO);
            return;
        }

        yield put(fenixYmsActions.exportInitPending({ transactionId, requestId: response?.requestId ?? null }));
    } catch (error) {
        if (error?.response?.status === CONFLICT_STATUS) {
            yield put(fenixYmsActions.discardPendingExport({ transactionId }));
            yield put(fenixYmsActions.fetchHistoryStart({ transactionId }));
            return;
        }

        const message = error?.response?.data?.message || error?.message || FENIX_YMS_COPY.exportFailed;
        yield put(fenixYmsActions.exportInitFailed({ transactionId, message }));
        yield call(autoSuggestExportedToYms, transactionId, YES_NO_UNKNOWN.NO);
    }
}

function* fetchFenixHistorySaga({ payload: { transactionId } }) {
    if (!transactionId) return;
    try {
        const data = yield call(getFenixYmsHistory, transactionId);
        const rawEntries = Array.isArray(data) ? data : data?.attempts || [];
        const attempts = rawEntries.map((entry, index) => normalizeAttempt(entry, index));
        yield put(fenixYmsActions.historyLoaded({ transactionId, attempts }));
    } catch {
        yield put(fenixYmsActions.historyLoadFailed({ transactionId }));
    }
}

function* exportResponseReceivedSaga({ payload: { transactionId, exportedToYms } }) {
    if (!transactionId) return;
    yield call(autoSuggestExportedToYms, transactionId, exportedToYms);
}

function* cleanupFenixStateSaga({ payload }) {
    const { transactionId, closeAllPanels } = payload || {};
    if (closeAllPanels) {
        yield put(fenixYmsActions.resetAll());
        return;
    }
    if (transactionId != null) {
        yield put(fenixYmsActions.removeTransaction(transactionId));
    }
}

export function* fenixYmsSaga() {
    yield fork(takeLeadingPerKey, fenixYmsActions.exportToYmsStart.type, exportToYmsStartSaga, (action) => action.payload?.transaction?.id);
    yield fork(takeLeadingPerKey, fenixYmsActions.fetchHistoryStart.type, fetchFenixHistorySaga, (action) => action.payload?.transactionId);
    yield takeEvery(fenixYmsActions.exportResponseReceived.type, exportResponseReceivedSaga);
    yield takeEvery(transactionActions.closeTransactionPanel.type, cleanupFenixStateSaga);
}
